/**
 * A lead, as a card in somebody's phone.
 *
 * Jonathon asked for every lead entered into Roof Board to also be a contact on
 * his iPhone, carrying the address, a link to the job's CompanyCam album and a
 * link back into Roof Board. A web app cannot write to iOS Contacts, so the
 * route is his Mac: a script reads what this module produces, writes it into
 * macOS Contacts, and iCloud carries it to the phone (ADR 0108).
 *
 * This file is the mapping and nothing else — no network, no Supabase, no
 * AppleScript. The Edge Function reads the rows and the Mac script writes the
 * card; what a card *says* is decided once, here, so that the two sides cannot
 * drift (CLAUDE.md rule 5).
 *
 * Three decisions are worth reading before changing anything:
 *
 *   1. **A contact is the unit, not a job.** One person with three jobs is one
 *      card in the phone, because that is how a phone rings. The job-shaped
 *      facts — the address, the CompanyCam album — come from their most recent
 *      job, which is the one a call is almost always about.
 *   2. **The card carries its own revision.** `revision` is a digest of
 *      everything the phone would show. The Mac keeps the last revision it
 *      wrote and skips a card that has not changed, so a sync that runs every
 *      minute does not rewrite four hundred contacts every minute.
 *   3. **Nothing here deletes.** A card that stops being exported is left alone
 *      in the phone. Deleting somebody's contact because a row moved in a CRM
 *      is not a thing worth being clever about.
 */

import { formatPhone } from "./phone.js";

/** The contact columns a card is built from. */
export interface PhoneContactSource {
  id: string;
  full_name: string | null;
  phone?: string | null;
  cell?: string | null;
  email?: string | null;
  address?: string | null;
  city?: string | null;
  state?: string | null;
  zip?: string | null;
  lead_source?: string | null;
}

/** The job columns a card is built from. */
export interface PhoneContactJob {
  id: string;
  contact_id: string;
  job_number?: number | null;
  is_test?: boolean | null;
  address?: string | null;
  city?: string | null;
  state?: string | null;
  zip?: string | null;
  companycam_project_url?: string | null;
  created_at?: string | null;
}

/** One card, in the shape the Mac script writes into Contacts. */
export interface PhoneContactCard {
  /** The Roof Board contact id. The card's identity, written into its note. */
  id: string;
  first: string;
  last: string;
  /** Set instead of a person's name when the lead is a business. */
  organization: string | null;
  /** True when the card should be filed under the organization. */
  isCompany: boolean;
  mobile: string | null;
  phone: string | null;
  email: string | null;
  street: string | null;
  city: string | null;
  state: string | null;
  zip: string | null;
  /** Deep link into Roof Board. Always present. */
  roofBoardUrl: string;
  /** The most recent job's CompanyCam album, when it has one. */
  companycamUrl: string | null;
  /** What the phone shows under Notes, including the identity marker. */
  note: string;
  /**
   * How to tell whether this person is already in the address book.
   *
   * Their numbers as bare ten-digit strings, and their email lowercased. The
   * Mac compares these against the cards that are already there and adopts a
   * match rather than making a second one. Not part of the revision: every one
   * of them is derived from a field that already is, so including them would
   * add nothing but a reason to rewrite every card the day this shipped.
   */
  matchKeys: string[];
  /** Digest of everything above. Unchanged digest means nothing to write. */
  revision: string;
}

/**
 * The line that makes a card findable again.
 *
 * Written into the note, because Contacts has no field of our own to hang an id
 * on and matching by name would merge two Bill Bakers into one. A card without
 * this line was made by a person and is never touched.
 */
export const CONTACT_MARKER_PREFIX = "roofboard:";

/** The group every exported card is filed under, so the set stays separable. */
export const PHONE_CONTACT_GROUP = "Roof Board";

/** The marker line for one contact. */
export function contactMarker(contactId: string): string {
  return `${CONTACT_MARKER_PREFIX}${contactId}`;
}

/** The contact id in a note, or null if it was not one of ours. */
export function markerContactId(
  note: string | null | undefined,
): string | null {
  if (!note) return null;
  for (const line of note.split(/\r?\n/)) {
    const trimmed = line.trim();
    if (trimmed.startsWith(CONTACT_MARKER_PREFIX)) {
      const id = trimmed.slice(CONTACT_MARKER_PREFIX.length).trim();
      return id === "" ? null : id;
    }
  }
  return null;
}

/**
 * Words that mean the lead is a business rather than a person.
 *
 * A commercial lead is filed under its name — "Perimeter Church" is not a
 * person called Perimeter with the surname Church, and splitting it produces a
 * card that sorts under P and reads as a stranger. The list is deliberately
 * short: a false positive files a person's card under a company name, which is
 * ugly, while a false negative is only a slightly odd sort order.
 */
const COMPANY_TOKENS = new Set([
  "llc",
  "l.l.c.",
  "inc",
  "inc.",
  "incorporated",
  "corp",
  "corp.",
  "corporation",
  "company",
  "ltd",
  "ltd.",
  "lp",
  "llp",
  "pllc",
  "properties",
  "property",
  "holdings",
  "management",
  "apartments",
  "church",
  "hoa",
  "association",
  "school",
  "storage",
  "ministries",
]);

/** True when a name reads as a business. */
export function looksLikeCompany(name: string): boolean {
  return name
    .split(/\s+/)
    .some((word) => COMPANY_TOKENS.has(word.toLowerCase().replace(/,$/, "")));
}

/**
 * A full name split the way a phone wants it.
 *
 * First word is the first name and everything after it is the surname, which is
 * right for "Bill Baker" and for "Mary Ann Van Dyke" alike — the alternative
 * (last word is the surname) turns Van Dyke into Dyke. A single word is a
 * surname, so the card sorts under the word somebody would look for.
 */
export function splitName(full: string): { first: string; last: string } {
  const parts = full.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return { first: "", last: "" };
  if (parts.length === 1) return { first: "", last: parts[0]! };
  return { first: parts[0]!, last: parts.slice(1).join(" ") };
}

/** Roof Board's own deep link to a contact. */
export function contactUrl(appUrl: string, contactId: string): string {
  return `${appUrl.replace(/\/+$/, "")}/#contact=${contactId}`;
}

const clean = (value: string | null | undefined): string | null => {
  const trimmed = (value ?? "").trim();
  return trimmed === "" ? null : trimmed;
};

/**
 * One phone number, reduced to the thing two spellings of it have in common.
 *
 * The last ten digits, which is how `match_contact_by_phone` in the database
 * already decides two numbers are one number, and for the same reason: an
 * address book holds `+1 (251) 555-0147`, Roof Board holds `2515550147`, and a
 * comparison that misses that makes a second Bill Baker.
 *
 * Null for anything that is not a US-shaped number. A four-digit extension has
 * no ten digits to compare, and treating "0147" as an identity would match it
 * against every card that happens to end the same way.
 */
export function phoneMatchKey(value: string | null | undefined): string | null {
  const digits = (value ?? "").replace(/\D/g, "");
  return digits.length >= 10 ? digits.slice(-10) : null;
}

/**
 * Everything about a contact that says "this is the same person".
 *
 * Numbers first and email second, both of them things one human owns. Not the
 * name: two Bill Bakers is exactly the case this exists to survive, and not the
 * address either, because a landlord and a tenant share one.
 */
export function matchKeysFor(contact: {
  cell?: string | null;
  phone?: string | null;
  email?: string | null;
}): string[] {
  const keys = new Set<string>();
  for (const number of [contact.cell, contact.phone]) {
    const key = phoneMatchKey(number);
    if (key) keys.add(key);
  }
  const email = clean(contact.email);
  if (email) keys.add(email.toLowerCase());
  return [...keys].sort();
}

/**
 * A stable digest of a card.
 *
 * FNV-1a rather than a real hash: this is a change detector on one Mac, not a
 * security boundary, and `packages/core` may not reach for node:crypto. What it
 * has to be is *stable* — the same card must digest the same way next week, or
 * every contact is rewritten on every run.
 */
export function cardRevision(fields: readonly (string | null)[]): string {
  let hash = 0x811c9dc5;
  for (const field of fields) {
    for (const char of `${field ?? ""} `) {
      hash ^= char.codePointAt(0)!;
      hash = Math.imul(hash, 0x01000193) >>> 0;
    }
  }
  return hash.toString(16).padStart(8, "0");
}

/**
 * The job whose details the card carries.
 *
 * The most recently created real job. A test lead is never it, and a contact
 * whose *only* jobs are test leads gets no job-shaped facts at all — the same
 * rule that keeps a practice job out of CompanyCam and out of the accounts.
 */
export function cardJob(
  jobs: readonly PhoneContactJob[],
): PhoneContactJob | null {
  const real = jobs.filter((job) => job.is_test !== true);
  if (real.length === 0) return null;
  return real.reduce((latest, job) =>
    (job.created_at ?? "") > (latest.created_at ?? "") ? job : latest,
  );
}

/**
 * What the phone shows under Notes.
 *
 * The marker line is last so that the first thing a person reads is the useful
 * part. Nothing here is a secret: it is on his own phone either way.
 */
function noteFor(
  contact: PhoneContactSource,
  job: PhoneContactJob | null,
): string {
  const lines: string[] = [];
  if (job?.job_number != null) lines.push(`Job ${job.job_number}`);
  const source = clean(contact.lead_source);
  if (source) lines.push(`Lead source: ${source}`);
  lines.push(contactMarker(contact.id));
  return lines.join("\n");
}

/**
 * One card.
 *
 * The address is the job's when the job has one and the contact's otherwise: a
 * customer's card should point at the roof being worked on, and the contact row
 * is often the billing address or empty. The four address fields move together
 * — taking the street from the job and the city from the contact would produce
 * an address that exists nowhere.
 */
export function phoneContactCard(
  contact: PhoneContactSource,
  jobs: readonly PhoneContactJob[],
  appUrl: string,
): PhoneContactCard {
  const job = cardJob(jobs);
  const name = (contact.full_name ?? "").trim();
  const isCompany = name !== "" && looksLikeCompany(name);
  const { first, last } = isCompany ? { first: "", last: "" } : splitName(name);

  const where = clean(job?.address) ? job! : contact;
  const mobile = clean(contact.cell);
  const phone = clean(contact.phone);

  const card: Omit<PhoneContactCard, "revision"> = {
    id: contact.id,
    first,
    last,
    organization: isCompany ? name : null,
    isCompany,
    mobile: mobile ? formatPhone(mobile) : null,
    phone: phone ? formatPhone(phone) : null,
    email: clean(contact.email),
    street: clean(where.address),
    city: clean(where.city),
    state: clean(where.state),
    zip: clean(where.zip),
    roofBoardUrl: contactUrl(appUrl, contact.id),
    companycamUrl: clean(job?.companycam_project_url),
    note: noteFor(contact, job),
    matchKeys: matchKeysFor(contact),
  };

  return {
    ...card,
    revision: cardRevision([
      card.first,
      card.last,
      card.organization,
      card.isCompany ? "company" : "person",
      card.mobile,
      card.phone,
      card.email,
      card.street,
      card.city,
      card.state,
      card.zip,
      card.roofBoardUrl,
      card.companycamUrl,
      card.note,
    ]),
  };
}

/**
 * Every card, from every contact.
 *
 * A contact with no name at all is skipped: a nameless card in a phone is
 * noise, and there is nothing to ring. Everything else goes, including a lead
 * nobody has called yet — that was the ask, so that when a stranger's number
 * lights up the screen it already says who they are.
 */
export function phoneContactCards(
  contacts: readonly PhoneContactSource[],
  jobs: readonly PhoneContactJob[],
  appUrl: string,
): PhoneContactCard[] {
  const byContact = new Map<string, PhoneContactJob[]>();
  for (const job of jobs) {
    const list = byContact.get(job.contact_id);
    if (list) list.push(job);
    else byContact.set(job.contact_id, [job]);
  }

  const cards: PhoneContactCard[] = [];
  for (const contact of contacts) {
    if ((contact.full_name ?? "").trim() === "") continue;
    cards.push(
      phoneContactCard(contact, byContact.get(contact.id) ?? [], appUrl),
    );
  }
  return cards;
}
